import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Check, Search } from 'lucide-react';

import { CAPABILITY_OPTIONS } from '@/api/capabilities.api';
import type { EmployeeListItem } from '@/api/employees.api';
import { assignSeat, getAccessPreview, type SeatView } from '@/api/seats.api';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { roleLabel } from '@/lib/accountability/seatMeta';

/**
 * Assign an employee to a seat. The picker → an access-preview fetch →
 * the access delta ("sets role to Department Lead · grants Manage training")
 * shown BEFORE the confirm, so admins see exactly what seating grants.
 * Directory-only employees show "pending portal account".
 */

const CAP_LABEL = new Map(CAPABILITY_OPTIONS.map((c) => [c.value as string, c.label]));

export interface AssignSeatModalProps {
  open: boolean;
  seat: SeatView | null;
  /** Candidate employees (the page passes the directory; benched people
   *  are included). */
  employees: EmployeeListItem[];
  onClose: () => void;
  onAssigned: (seat: SeatView) => void;
}

export function AssignSeatModal({ open, seat, employees, onClose, onAssigned }: AssignSeatModalProps) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = employees.filter((e) => e.isActive);
    if (!q) return base.slice(0, 50);
    return base
      .filter((e) => `${e.firstName} ${e.lastName} ${e.email} ${e.jobTitle ?? ''}`.toLowerCase().includes(q))
      .slice(0, 50);
  }, [employees, search]);

  const selected = useMemo(() => employees.find((e) => e.id === selectedId) ?? null, [employees, selectedId]);

  const previewQuery = useQuery({
    queryKey: ['seats', 'access-preview', seat?.id, selectedId],
    queryFn: () => getAccessPreview(seat?.id ?? '', selectedId ?? ''),
    enabled: open && seat !== null && selectedId !== null,
    retry: false,
  });

  const assignMutation = useMutation({
    mutationFn: () => assignSeat(seat?.id ?? '', selectedId ?? ''),
    onSuccess: (updated) => {
      onAssigned(updated);
      handleClose();
    },
    onError: (err) => {
      setError(assignMessage(err));
    },
  });

  function handleClose(): void {
    setSearch('');
    setSelectedId(null);
    setError(null);
    onClose();
  }

  if (!seat) return null;

  const preview = previewQuery.data;

  return (
    <Modal open={open} onClose={handleClose} title={`Assign to “${seat.name}”`} size="md">
      <div className="space-y-4">
        {seat.holderEmployeeId ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-warning-text" />
            <p className="text-[12px] text-kw-warning-text">
              This seat is occupied. Assigning someone new replaces the current holder.
            </p>
          </div>
        ) : null}

        {/* Search */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-kw-muted" />
          <input
            aria-label="Search people"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            placeholder="Search people…"
            className="w-full rounded-kw-md border border-kw-border bg-white py-2 pl-9 pr-3 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </div>

        {/* People list */}
        <div className="max-h-52 space-y-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-1 py-4 text-center text-[13px] text-kw-muted">No matching people.</p>
          ) : (
            filtered.map((e) => {
              const active = e.id === selectedId;
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => {
                    setSelectedId(e.id);
                    setError(null);
                  }}
                  className={`flex w-full items-center gap-2 rounded-kw-md border px-2.5 py-2 text-left transition-colors ${
                    active ? 'border-kw-cerulean bg-kw-bg-blue' : 'border-transparent hover:bg-kw-bg'
                  }`}
                >
                  <span className="grid size-7 shrink-0 place-items-center rounded-kw-full bg-kw-cerulean text-[11px] font-medium text-white">
                    {`${e.firstName.charAt(0)}${e.lastName.charAt(0)}`.toUpperCase() || '·'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-kw-shark">
                      {e.firstName} {e.lastName}
                    </span>
                    <span className="block truncate text-[11px] text-kw-muted">{e.jobTitle ?? e.email}</span>
                  </span>
                  {active ? <Check className="size-4 shrink-0 text-kw-cerulean" /> : null}
                </button>
              );
            })
          )}
        </div>

        {/* Access delta preview */}
        {selected ? (
          <div className="rounded-kw-md border border-kw-border bg-kw-bg p-3">
            <p className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Access preview</p>
            {previewQuery.isLoading ? (
              <p className="mt-1 text-[13px] text-kw-muted">Checking what this grants…</p>
            ) : previewQuery.error ? (
              <p className="mt-1 text-[13px] text-kw-danger-text">
                Could not load the access preview. You can still assign; the server applies access on save.
              </p>
            ) : preview ? (
              <div className="mt-1 space-y-1.5 text-[13px] text-kw-shark">
                <p className="flex flex-wrap items-center gap-1.5">
                  {preview.roleBefore === preview.roleAfter ? (
                    <span>
                      Role stays <strong>{roleLabel(preview.roleAfter)}</strong>.
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      Sets role {roleLabel(preview.roleBefore)}
                      <ArrowRight className="size-3.5 text-kw-muted" />
                      <strong>{roleLabel(preview.roleAfter)}</strong>
                    </span>
                  )}
                </p>
                {preview.capabilitiesAdded.length > 0 ? (
                  <p>
                    Grants{' '}
                    <span className="font-medium">
                      {preview.capabilitiesAdded.map((c) => CAP_LABEL.get(c) ?? c).join(', ')}
                    </span>
                    .
                  </p>
                ) : (
                  <p className="text-kw-muted">No new capabilities beyond the role.</p>
                )}
                {preview.capabilitiesAlreadyHeld.length > 0 ? (
                  <p className="text-[12px] text-kw-muted">
                    Already holds:{' '}
                    {preview.capabilitiesAlreadyHeld.map((c) => CAP_LABEL.get(c) ?? c).join(', ')}.
                  </p>
                ) : null}
                {preview.accessPendingInvitation ? (
                  <p className="text-[12px] italic text-kw-warning-text">
                    Pending portal account — access applies once they accept their invite.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-[12px] text-kw-muted">Pick someone to preview the access they’ll get.</p>
        )}

        {error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{error}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-kw-border pt-4">
          <Button type="button" variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!selectedId}
            isLoading={assignMutation.isPending}
            onClick={() => {
              assignMutation.mutate();
            }}
          >
            {seat.holderEmployeeId ? 'Replace holder' : 'Assign seat'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function assignMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number; data?: { error?: unknown } } }).response;
    if (response?.status === 409 || response?.data?.error === 'seat_occupied') {
      return 'Someone was just seated here. Reload the chart and try again.';
    }
    const code = response?.data?.error;
    if (typeof code === 'string') return `Could not assign the seat (${code}).`;
  }
  return 'Could not assign the seat — please try again.';
}
